// src/components/device/DeviceCommands.jsx
import React, { useState } from "react";
import { Box, Button, Alert, Stack, Typography, Card, CardContent, Chip } from "@mui/material";
import TerminalIcon from "@mui/icons-material/Terminal";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";

import SendCommandModal from "../../SendCommandModal"; // ✅ adjust path
import CommandAccessManagerModal from "./CommandAccessManagerModal";

export default function DeviceCommands({ device, user, assetsData, canSendCommands, canSendRaw }) {
  const [open, setOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [initialCommandType, setInitialCommandType] = useState("");
  const canManageAccess = !!user;

  if (!canSendCommands) {
    return (
      <Alert icon={<BlockIcon />} severity="error">
        You are not authenticated to send commands.
      </Alert>
    );
  }

  const isOffline = (device?.status || "").toLowerCase() !== "online";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Command Center
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Send commands quickly. Engine OFF/ON is available at the top of command panel.
              </Typography>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip label={`Role: ${user?.role || "-"}`} variant="outlined" />
                <Chip label={`Raw: ${canSendRaw ? "Enabled" : "Enabled (server validated)"}`} color="success" variant="filled" />
              </Stack>
            </Box>

            <Button
              variant="contained"
              startIcon={<TerminalIcon />}
              onClick={() => {
                setInitialCommandType("");
                setOpen(true);
              }}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
            >
              Open Command Center
            </Button>
            <Button
              variant="outlined"
              startIcon={<CallIcon />}
              onClick={() => {
                setInitialCommandType("audio_monitor");
                setOpen(true);
              }}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Call / Listen
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CallEndIcon />}
              onClick={() => {
                setInitialCommandType("stop_audio_monitor");
                setOpen(true);
              }}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Stop Call
            </Button>
            {canManageAccess && (
              <Button
                variant="outlined"
                startIcon={<ManageAccountsIcon />}
                onClick={() => setAccessOpen(true)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Manage Access
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {isOffline && (
        <Alert icon={<WarningAmberIcon />} severity="warning">
          Device is offline. Command may fail fast; retry once device comes online.
        </Alert>
      )}

      {open && (
        <SendCommandModal
          device={device}
          onClose={() => setOpen(false)}
          onCommandSent={() => {}}
          assetsData={assetsData || []}
          initialCommandType={initialCommandType}
          // Optional flags you can use inside SendCommandModal UI
          canSendRaw={canSendRaw}
        />
      )}

      {accessOpen && (
        <CommandAccessManagerModal
          open={accessOpen}
          onClose={() => setAccessOpen(false)}
          user={user}
        />
      )}
    </Box>
  );
}
